import type { FieldValues, Path, UseFormReturn } from "react-hook-form";

import { useCountries } from "@/api/hooks";
import type { Country } from "@/api/types";
import { TextField } from "@/components/ui";

type LocationValues = { country: string; state: string; tax_id: string };

export function findCountry(countries: Country[] | undefined, value: string | null | undefined): Country | undefined {
  if (!value || !countries) return undefined;
  const v = value.trim().toLowerCase();
  return countries.find((c) => c.code.toLowerCase() === v || c.name.toLowerCase() === v);
}

/** "IN" -> "India"; unknown codes are shown as stored. */
export function useCountryName(): (code: string | null | undefined) => string | null {
  const { data } = useCountries();
  return (code) => (code ? findCountry(data, code)?.name ?? code : null);
}

/**
 * Country, state and tax ID together: the country decides which states are offered and what
 * the tax number is called (GSTIN, VAT number, ABN…). The server validates the number's format
 * and checksum for that country.
 */
export function LocationFields<T extends FieldValues & LocationValues>({
  form, errors, taxHint,
}: { form: UseFormReturn<T>; errors: Record<string, string>; taxHint?: string }) {
  const { data: countries } = useCountries();
  const { register, watch } = form;
  const name = (k: keyof LocationValues) => k as Path<T>;
  const country = findCountry(countries, watch(name("country")) as string);
  const state = (watch(name("state")) as string) ?? "";
  const knownState = !state || country?.states.some((s) => s.name === state);
  const raw = (watch(name("country")) as string) ?? "";

  return (
    <>
      <div>
        <label htmlFor="country" className="label">Country</label>
        <select id="country" className="input" aria-invalid={!!errors.country} {...register(name("country"))}>
          <option value="">Not set</option>
          {raw && !country && <option value={raw}>{raw}</option>}
          {countries?.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
        </select>
        {errors.country && <p className="field-error">{errors.country}</p>}
      </div>
      {country && country.states.length > 0 && knownState ? (
        <div>
          <label htmlFor="state" className="label">{country.code === "CA" ? "Province / territory" : "State / territory"}</label>
          <select id="state" className="input" aria-invalid={!!errors.state} {...register(name("state"))}>
            <option value="">Not set</option>
            {country.states.map((s) => <option key={s.code} value={s.name}>{s.name}</option>)}
          </select>
          {errors.state && <p className="field-error">{errors.state}</p>}
        </div>
      ) : (
        <TextField id="state" label="State / region" {...register(name("state"))} error={errors.state} />
      )}
      <TextField
        id="tax_id"
        label={country ? country.tax_id_label : "Tax ID"}
        hint={taxHint ?? (country ? `${country.name}: ${country.tax_name} registration number` : "Tax registration number")}
        {...register(name("tax_id"))}
        error={errors.tax_id}
      />
    </>
  );
}
